import { syncDrivenVersions, type DrivenVersionFields } from '@agavi/blocks';
import { useEffect, useRef } from 'react';

import { versionStructure } from './use-block-versions';
import { useDriverRows } from './use-driver-rows';

import type { Block } from '@/types/experience';

/**
 * KEEP A DRIVEN BLOCK'S VERSIONS EQUAL TO THE DRIVER'S ROWS — the projection, made continuous.
 *
 * The sibling of `useScenarioRowProjection`, and written against the same lesson it records:
 * applying a projection once at link time is not a projection. Add an option to the driver and
 * the driven block would still hold the old versions while the row set it is meant to mirror
 * had grown, so the new option would select nothing and fall through to whatever version
 * happened to be first.
 *
 * ## ONE LINE PER BLOCK, and nothing block-shaped in it
 *
 * Twelve blocks mount this and none of them describes its own answer key to it. The first
 * version took a `template` callback — the block's question, its feedback and its key, written
 * out — and that would have been twelve hand-rolled copies of a thing the blocks already own,
 * drifting the way the link control's four props drifted before they were collapsed.
 *
 * What makes it unnecessary is `addVersionWith`'s existing behaviour. Every block has an
 * `add…Version` of exactly one shape, `(data, fromVersionId, newId) => data`, and asked for a
 * version with no `fromVersionId` it returns a bank whose FIRST entry is already what a new
 * row should copy:
 *
 *   - no bank yet  — version one is the block's current setup, migrated
 *   - a bank       — version one is version one
 *
 * Which is the rule this projection wants, both halves of it, derived rather than restated.
 * Copying VERSION ONE rather than the version in force is also deliberate and differs from
 * `+ Version`: that duplicates what the teacher is looking at because they pressed it while
 * looking at it, and this fires on its own, from a change in another task, where copying
 * whatever happened to be in force would make one edit produce different content depending on
 * where the teacher had last paged.
 *
 * ## It writes, so it is an effect
 *
 * The versions are persisted data. Deriving them per render would leave what is stored
 * disagreeing with what is shown, which is the drift the feature exists to prevent.
 *
 * `versionStructure` and not the block's field writer: a projection REPLACES the version list,
 * and every block's field writer routes through a `write…Version` that folds its payload INTO
 * the version in force — so the list would have been folded into one of its own members.
 *
 * ## The driver is addressed, not held
 *
 * `drivenBy` names a BLOCK or an ALIAS, and `useDriverRows` resolves either into one list of
 * rows — the union of the members' options, for an alias. The task list comes from the recall
 * scope, the same provider the chips read, absent in Preview, which is exactly where this
 * must not run. An address that no longer resolves leaves the versions ALONE: that is the
 * orphaned state, and `versionAxisOwner` reports it from the row ids the versions still
 * carry. Rebuilding or clearing them here is the one thing that would lose the teacher's
 * work.
 */
export function useDrivenVersionProjection<
  V extends { id: string } & DrivenVersionFields,
  D extends { versions?: V[]; drivenBy?: string },
>({ block, data, emit, addVersion }: {
  block: Block;
  /** The block's own normalised data — `storedData` at every call site. */
  data: D;
  emit: (next: Record<string, unknown>) => void;
  /** The block's OWN `add…Version`. It already knows the answer key; see the note above. */
  addVersion: (data: D, fromVersionId: string | undefined, newId: () => string) => D;
}) {
  const { drivenBy, versions } = data;
  /* THROUGH `useDriverRows`, which owns the block-or-alias split and the union. A second
     derivation here would be a second answer to "what rows is this axis authored against",
     and the two disagreeing is a block whose versions answer options it does not have. */
  const { rows } = useDriverRows(drivenBy);

  // The identity the effect watches: WHICH rows exist, not the objects holding them. A row's
  // LABEL is deliberately in it — renaming an option renames the version, and the teacher
  // should see that without reloading.
  const key = (rows ?? []).map(r => `${r.id}\u0001${r.label}`).join('\u0000');
  const have = (versions ?? []).map(v => v.drivenRowId ?? '').join('\u0000');

  /* Through refs, because each is a fresh closure every render and none of them is an identity
     worth re-running on — the same arrangement `useScenarioRowProjection` settled on. */
  const liveRef = useRef({ block, data, emit, addVersion, rows, versions });
  liveRef.current = { block, data, emit, addVersion, rows, versions };
  /**
   * The address this block was pointed at last time the effect ran.
   *
   * `drivenBy` is in the dep array, so the effect knows WHEN it changed and not what it
   * changed FROM — and "did the teacher re-point this" is the whole question. Undefined on the
   * first run, which is a block arriving already driven: that is not a switch and must not
   * discard the bank it arrived with.
   */
  const previousDriver = useRef<string | undefined>(undefined);

  useEffect(() => {
    const live = liveRef.current;
    // Not driven, or a driver that cannot partition: the block keeps exactly what it holds.
    if (!drivenBy || !live.rows) return;
    const newId = () => crypto.randomUUID();
    /*
     * ONE call for the whole sync, outside the template. Asked per missing row it would mint a
     * fresh copy of the same source each time, which is wasted work and — because
     * `addVersionWith` deep-copies branches with new ids — a different object each call for no
     * reason. `syncDrivenVersions` gives each row its own id and row id regardless.
     */
    const from = live.addVersion(live.data, undefined, newId).versions?.[0];
    if (!from) return;
    /*
     * RE-POINTING IS NOT A SYNC, and `syncDrivenVersions` cannot tell them apart.
     *
     * It KEEPS a version whose row is gone and appends it after the live ones — right for its
     * design case, one option deleted, where the authored question should survive in case the
     * option comes back. On a driver SWITCH every old row vanishes at once and none of them
     * will ever return, so the old bank piles up behind the new one.
     *
     * The function sees rows and versions; only the CALLER knows the address changed. So the
     * caller drops the old bank here.
     *
     * ## THE WHOLE BANK, authored or not
     *
     * This used to discard only a bank that was entirely `unreviewed` — projected copies
     * nobody had touched — on the reasoning that silently deleting a teacher's questions to
     * tidy a list is the worse failure.
     *
     * The gate never fired. `unreviewed` comes off the moment a version is edited, so one
     * authored question anywhere kept the ENTIRE bank, and the kept orphans read as authored
     * forever after: every later switch piled more on. Read straight out of the dev database
     * after the earlier fixes, a Numerical held five versions for a driver with four rows,
     * the fifth answering an option of some previous driver. Reported twice — "this is
     * blowing up my answer pile ... it's at 13 now", then "i have set up driven by behavior
     * for one mcq. i switched to another. the question versions are accumulating instead of
     * clearing".
     *
     * Dropping the lot is also the bargain the axis already makes one control over:
     * `collapseDrivenAxis`, which is what None does, keeps version one and discards every
     * alternative whether or not it was authored. A switch is the same deliberate act said
     * differently, and the questions it drops answer options that no longer exist.
     *
     * Still not silent: the write is in the undo stack like any other.
     *
     * A version parked by an option DELETE is untouched by this — that is not a switch, the
     * option may come back, and `driven-version-stranded` reports it meanwhile.
     */
    const switched = previousDriver.current !== undefined && previousDriver.current !== drivenBy;
    previousDriver.current = drivenBy;
    const carried = switched ? undefined : live.versions;
    const { versions: next } = syncDrivenVersions<V>(
      live.rows, carried, () => ({ ...from, id: newId() }),
    );
    // Nothing to do is the common case: every render of a driven block reaches here.
    if (next.length === (live.versions?.length ?? 0)
      && next.every((v, i) => v === live.versions?.[i])) return;
    live.emit(versionStructure(live.block.data, { versions: next }));
    // Only the three identities below, and no suppression needed for it: everything else the
    // effect touches comes through `liveRef`, which is also WHY it does. Re-running on every
    // data change would rewrite the versions out from under live typing, exactly as
    // `useVersionSeeding` records for its own pointer.
  }, [drivenBy, key, have]);
}
